import { Favorite } from 'lib/services/favorites-service';
import { normalizeGuid } from 'lib/utils/string-utils';
import { useMemo } from 'react';
import { useSessionSafe } from 'lib/hooks/auth/useSessionSafe';

export interface UseFavoritesApiIdsOptions {
  /** Whether the hook should fetch at all. Returns [] immediately when false. */
  enabled: boolean;
  /** Optional content-type filter (case-insensitive). Pass undefined to include all types. */
  contentType?: string;
}

/**
 * Returns the authenticated user's saved favorite content IDs from the session.
 *
 * The NextAuth session callback already fetches and caches favorites on
 * `session.user.favorites`, so we read directly from there — no extra API call needed.
 *
 * Returns:
 *  - `null`      → session is still loading
 *  - `[]`        → not enabled, unauthenticated, or no matching favorites
 *  - `string[]`  → resolved favorite content IDs (optionally filtered by contentType)
 */
export function useFavoritesApiIds({
  enabled,
  contentType,
}: UseFavoritesApiIdsOptions): string[] | null {
  const { data: session, status } = useSessionSafe();

  const favorites = (session?.user?.favorites as Favorite[] | undefined) ?? [];

  // 'All' means no content-type filter — same as undefined
  const effectiveContentType =
    !contentType || contentType.toLowerCase() === 'all' ? undefined : contentType;

  // Stable primitive key — only recreates the IDs array when the underlying data changes,
  // preventing a new array reference on every render which would cause an infinite effect loop.
  const favoritesKey = favorites
    .map((fav) => `${fav.Content?.Id}:${fav.Content?.ContentType}`)
    .join('|');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const ids = useMemo(
    () =>
      favorites
        .filter((fav) => {
          const id = fav.Content?.Id;
          const type = fav.Content?.ContentType;

          if (!id) return false;
          if (!effectiveContentType || !type) return true;

          return type.toLowerCase() === effectiveContentType.toLowerCase();
        })
        .map((fav) => normalizeGuid(fav.Content.Id) || fav.Content.Id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [favoritesKey, effectiveContentType]
  );

  if (!enabled) return [];
  if (status === 'loading') return null;
  if (status !== 'authenticated') return [];

  return ids;
}
